import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { Lender, LenderUser, UpdateLenderRequest } from '../../api/lenders';
import { checkSalesforceIdUnique } from '../../api/lenderOnboarding';
import { useLenderLogo, useLenderUsers, useUpdateLender, useUploadLenderLogo } from '../../hooks/useLenders';
import { lenderAccountSchema, type LenderAccountValues } from './lenderAccount.schema';
import { SALESFORCE_ID_DUPLICATE } from './wizard/copy';
import { US_STATES } from './wizard/usStates';
import avatarPlaceholder from './assets/lender-avatar-placeholder.svg';
import uploadIcon from './assets/lender-upload-photo.svg';

const PHOTO_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const PHOTO_MAX_BYTES = 2 * 1024 * 1024;
const PHOTO_INVALID = 'The photo must be a PNG, JPG or WEBP image up to 2 MB';
const SAVE_FAILED = 'Unable to save the changes. Please try again.';

const toValues = (lender: Lender): LenderAccountValues => ({
  lenderName: lender.displayName ?? '',
  salesforceId: lender.salesforceId ?? '',
  nmlsId: lender.nmlsId ?? '',
  primaryContact: lender.primaryContact ?? '',
  street: lender.address?.street ?? '',
  city: lender.address?.city ?? '',
  state: lender.address?.state ?? '',
  zip: lender.address?.zip ?? '',
});

/** Only what differs from the stored lender; the address travels whole when any line moved. */
const changes = (initial: LenderAccountValues, values: LenderAccountValues): UpdateLenderRequest => {
  const data: UpdateLenderRequest = {};
  if (values.lenderName !== initial.lenderName) data.displayName = values.lenderName;
  if (values.salesforceId !== initial.salesforceId) data.salesforceId = values.salesforceId;
  if (values.nmlsId !== initial.nmlsId) data.nmlsId = values.nmlsId;
  if (values.primaryContact !== initial.primaryContact) data.primaryContact = values.primaryContact;
  const addressKeys = ['street', 'city', 'state', 'zip'] as const;
  if (addressKeys.some((key) => values[key] !== initial[key])) {
    data.address = { street: values.street, city: values.city, state: values.state, zip: values.zip };
  }
  return data;
};

/** "Jane Doe - jane@acme.com"; a user's `name` holds the email, so without a first/last name only the email shows. */
const contactLabel = (user: LenderUser): string => {
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ');
  return fullName ? `${fullName} - ${user.email}` : user.email;
};

/** An object URL for a Blob, revoked when the Blob changes or the component goes away. */
function useObjectUrl(blob: Blob | null | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob) {
      setUrl(null);
      return;
    }
    const next = URL.createObjectURL(blob);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [blob]);
  return url;
}

export default function LenderAccountTab({ lender }: { lender: Lender }) {
  const { data: users } = useLenderUsers(lender.urn);
  const { data: logo } = useLenderLogo(lender.urn);
  const updateLender = useUpdateLender();
  const uploadLogo = useUploadLenderLogo();

  const readOnly = lender.status === 'blocked';

  // The values the form compares against: the stored lender, or what was just saved while the
  // refetch that brings it back is still in flight.
  const [initial, setInitial] = useState(() => toValues(lender));
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [duplicate, setDuplicate] = useState(false);
  const [checkedId, setCheckedId] = useState<string | null>(null);
  const photoInput = useRef<HTMLInputElement>(null);

  const { register, watch, reset, setValue, getValues, handleSubmit, formState: { errors, isDirty, isValid } } =
    useForm<LenderAccountValues>({
      resolver: zodResolver(lenderAccountSchema),
      mode: 'onChange',
      defaultValues: initial,
    });

  // A refetch (after a save, or on window focus) brings the stored values in, but never over edits.
  useEffect(() => {
    if (isDirty) return;
    const stored = toValues(lender);
    setInitial(stored);
    reset(stored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lender]);

  // The select only shows the stored contact once its option exists, and the users usually
  // arrive after the form applied its values.
  useEffect(() => {
    if (users?.length) setValue('primaryContact', getValues('primaryContact'));
  }, [users, setValue, getValues]);

  const photoUrl = useObjectUrl(photo);
  const logoUrl = useObjectUrl(logo);

  // Same live check as the wizard, except for the id the lender already holds.
  const salesforceId = watch('salesforceId');
  const needsCheck = !!salesforceId && !errors.salesforceId && salesforceId !== initial.salesforceId;
  useEffect(() => {
    let cancelled = false;
    if (!needsCheck) {
      setDuplicate(false);
      setCheckedId(null);
      return;
    }
    checkSalesforceIdUnique(salesforceId)
      .then((unique) => { if (!cancelled) { setDuplicate(!unique); setCheckedId(salesforceId); } })
      .catch(() => { if (!cancelled) { setDuplicate(false); setCheckedId(salesforceId); } });
    return () => { cancelled = true; };
  }, [salesforceId, needsCheck]);
  const awaitingCheck = needsCheck && checkedId !== salesforceId;

  const choosePhoto = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!PHOTO_TYPES.includes(file.type) || file.size > PHOTO_MAX_BYTES) {
      setPhoto(null);
      setPhotoError(PHOTO_INVALID);
      return;
    }
    setPhotoError(null);
    setPhoto(file);
  };

  const discard = () => {
    reset(initial);
    setPhoto(null);
    setPhotoError(null);
    setSaveError(null);
  };

  const onSave = handleSubmit(async (values) => {
    setSaving(true);
    setSaveError(null);
    try {
      const data = changes(initial, values);
      if (Object.keys(data).length > 0) {
        await updateLender.mutateAsync({ urn: lender.urn, data });
      }
      if (photo) {
        await uploadLogo.mutateAsync({ urn: lender.urn, file: photo });
      }
      setInitial(values);
      reset(values);
      setPhoto(null);
    } catch {
      setSaveError(SAVE_FAILED);
    } finally {
      setSaving(false);
    }
  });

  const hasChanges = isDirty || !!photo;
  // A refused photo is never pending, so it does not hold back the other changes.
  const canSave = !readOnly && hasChanges && isValid && !duplicate && !awaitingCheck && !saving;
  const avatarUrl = photoUrl ?? logoUrl;

  const errorText = (message?: string) =>
    message ? <p className="mt-1 text-sm text-red-500">{message}</p> : null;

  return (
    <form onSubmit={onSave} className="card rounded-xl p-10" noValidate>
      <fieldset disabled={readOnly} className="flex flex-col gap-8">
        <section className="flex flex-col gap-4">
          <h2 className="text-lg font-medium text-content">Primary Information</h2>
          <div className="flex items-center gap-6">
            <div className="flex size-[120px] shrink-0 items-center justify-center overflow-hidden rounded-full border border-border-subtle bg-surface-sunken">
              {avatarUrl
                ? <img src={avatarUrl} alt={`${lender.displayName} photo`} className="size-full object-cover" />
                : <img src={avatarPlaceholder} alt="" width={44} height={50} />}
            </div>
            <div>
              <button type="button" className="btn-secondary h-11 gap-2.5 px-6" onClick={() => photoInput.current?.click()}>
                <img src={uploadIcon} alt="" width={16} height={16} />
                Upload Photo
              </button>
              <input
                ref={photoInput}
                type="file"
                accept={PHOTO_TYPES.join(',')}
                aria-label="Upload Photo"
                className="sr-only"
                tabIndex={-1}
                onChange={choosePhoto}
              />
              {errorText(photoError ?? undefined)}
            </div>
          </div>
        </section>

        <div className="grid grid-cols-1 gap-x-2 gap-y-8 sm:grid-cols-2">
          <div className="field">
            <label className="label" htmlFor="lenderName">
              Lender Name <span className="text-red-500">*</span>
            </label>
            <input id="lenderName" className="input" {...register('lenderName')} />
            {errorText(errors.lenderName?.message)}
          </div>
          <div className="field">
            <label className="label" htmlFor="salesforceId">Salesforce ID</label>
            <input id="salesforceId" className="input" {...register('salesforceId')} />
            {errorText(errors.salesforceId?.message ?? (duplicate ? SALESFORCE_ID_DUPLICATE : undefined))}
          </div>
          <div className="field">
            <label className="label" htmlFor="nmlsId">NMLS ID</label>
            <input id="nmlsId" className="input" {...register('nmlsId')} />
            {errorText(errors.nmlsId?.message)}
          </div>
          <div className="field">
            <label className="label" htmlFor="primaryContact">Primary Contact</label>
            <select id="primaryContact" className="input" {...register('primaryContact')}>
              <option value="">Select a primary contact</option>
              {(users ?? []).map((user) => (
                <option key={user.urn} value={user.urn}>{contactLabel(user)}</option>
              ))}
            </select>
          </div>
        </div>

        <h2 className="text-lg font-medium text-content">Address</h2>
        <div className="grid grid-cols-1 gap-x-2 gap-y-8 sm:grid-cols-2">
          <div className="field">
            <label className="label" htmlFor="street">
              Street Address <span className="text-red-500">*</span>
            </label>
            <input id="street" className="input" {...register('street')} />
            {errorText(errors.street?.message)}
          </div>
          <div className="field">
            <label className="label" htmlFor="city">
              City <span className="text-red-500">*</span>
            </label>
            <input id="city" className="input" {...register('city')} />
            {errorText(errors.city?.message)}
          </div>
          <div className="field">
            <label className="label" htmlFor="state">
              State <span className="text-red-500">*</span>
            </label>
            <select id="state" className="input" {...register('state')}>
              <option value="">Select a state</option>
              {US_STATES.map(({ code, name }) => (
                <option key={code} value={code}>{name}</option>
              ))}
            </select>
            {errorText(errors.state?.message)}
          </div>
          <div className="field">
            <label className="label" htmlFor="zip">
              Zip <span className="text-red-500">*</span>
            </label>
            <input id="zip" className="input" {...register('zip')} />
            {errorText(errors.zip?.message)}
          </div>
        </div>

        {saveError && <p role="alert" className="text-sm text-red-500">{saveError}</p>}

        <div className="flex justify-end gap-2">
          <button type="button" className="btn-secondary h-11 px-6" onClick={discard} disabled={!hasChanges && !photoError}>
            Reset
          </button>
          <button type="submit" className="btn-primary h-11 px-6" disabled={!canSave}>
            Save
          </button>
        </div>
      </fieldset>
    </form>
  );
}
